import {
  Box,
  Button,
  Heading,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Link,
  Text,
  useColorModeValue,
  VStack,
} from '@elkaso-app/web-design';
import { useRefreshSiSheetsApi } from 'apis/supplier-intelligence';
import { useEffect, useState } from 'react';
import { FiBox, FiRefreshCw, FiSearch, FiTruck } from 'react-icons/fi';
import { SheetsOverviewSection } from '../components/sheets-overview-section';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { changeTitle } from 'redux/actions';
import { useAppDispatch } from 'redux/hooks';

const LandingScreen = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const match = useRouteMatch();
  const [supplierQuery, setSupplierQuery] = useState('');
  const [productQuery, setProductQuery] = useState('');
  const { mutate: refreshSheets, isLoading: isRefreshing } = useRefreshSiSheetsApi();
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  useEffect(() => {
    dispatch(changeTitle('Supplier Intelligence'));
  }, [dispatch]);

  const handleSupplierSearch = () => {
    const params = supplierQuery ? `?search=${encodeURIComponent(supplierQuery)}` : '';
    history.push(`${match.url}/suppliers${params}`);
  };

  const handleProductSearch = () => {
    const params = productQuery ? `?search=${encodeURIComponent(productQuery)}` : '';
    history.push(`${match.url}/products${params}`);
  };

  return (
    <Box maxW='1100px' mx='auto' mt={16}>
      <VStack spacing={2} mb={10} textAlign='center'>
        <Heading size='lg'>Supplier Intelligence</Heading>
        <Text color='gray.500'>Market intelligence for products and suppliers</Text>
      </VStack>

      <HStack spacing={6} align='stretch'>
        <Box flex={1} p={8} bg={cardBg} border='1px solid' borderColor={borderColor} borderRadius='xl' shadow='sm'>
          <VStack spacing={4} align='stretch'>
            <Icon as={FiTruck} boxSize={8} color='blue.500' />
            <Heading size='md'>Search by Supplier</Heading>
            <Text fontSize='sm' color='gray.500'>
              Explore a supplier&apos;s catalog, orders, and contact details
            </Text>
            <InputGroup>
              <InputLeftElement pointerEvents='none'>
                <FiSearch color='gray' />
              </InputLeftElement>
              <Input
                placeholder='Supplier name...'
                value={supplierQuery}
                onChange={(e) => setSupplierQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSupplierSearch()}
              />
            </InputGroup>
            <Link
              fontSize='sm'
              color='blue.500'
              onClick={() => history.push(`${match.url}/suppliers`)}
              cursor='pointer'>
              Browse All Suppliers
            </Link>
          </VStack>
        </Box>

        <Box flex={1} p={8} bg={cardBg} border='1px solid' borderColor={borderColor} borderRadius='xl' shadow='sm'>
          <VStack spacing={4} align='stretch'>
            <Icon as={FiBox} boxSize={8} color='green.500' />
            <Heading size='md'>Search by Product</Heading>
            <Text fontSize='sm' color='gray.500'>
              Find market intelligence for any product across all suppliers
            </Text>
            <InputGroup>
              <InputLeftElement pointerEvents='none'>
                <FiSearch color='gray' />
              </InputLeftElement>
              <Input
                placeholder='Product name...'
                value={productQuery}
                onChange={(e) => setProductQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleProductSearch()}
              />
            </InputGroup>
            <Link
              fontSize='sm'
              color='blue.500'
              onClick={() => history.push(`${match.url}/products`)}
              cursor='pointer'>
              Browse All Products
            </Link>
          </VStack>
        </Box>
      </HStack>

      <Box mt={6} p={6} bg={cardBg} border='1px solid' borderColor={borderColor} borderRadius='xl' shadow='sm'>
        <HStack spacing={4} justify='space-between' align='center'>
          <HStack spacing={4} align='center'>
            <Icon as={FiRefreshCw} boxSize={6} color='orange.500' />
            <Box>
              <Text fontWeight='semibold'>Sync Latest Data</Text>
              <Text fontSize='sm' color='gray.500'>
                Pull the latest supplier and product data from Google Sheets into the intelligence dashboard. Use this after
                updating the source sheets.
              </Text>
            </Box>
          </HStack>
          <Button
            size='sm'
            colorScheme='orange'
            variant='outline'
            leftIcon={<FiRefreshCw />}
            isLoading={isRefreshing}
            loadingText='Syncing...'
            onClick={() => refreshSheets()}
            flexShrink={0}>
            Refresh Data
          </Button>
        </HStack>
      </Box>

      <SheetsOverviewSection />
    </Box>
  );
};

export default LandingScreen;
